/**
 * Header Block Builder Alpine.js Component
 *
 * Handles header functionality including:
 * - Overlay header with transparent background and color inversion
 * - Scroll-based and hover background color changes
 * - Color scheme-based text colors
 * - Page-specific overlay restrictions
 */
export default {
  name: 'header',
  component(settings) {
    // Header overlay logic with transparent background and color inversion
    const overlayHeader = {
      isOverlay: false,
      isInverted: false,
      hasScrolled: false,
      isHovered: false,
      hasVisibleHeaderComponents: false,
      scrollThreshold: 0,
      handleScroll: null,
      handleMouseEnter: null,
      handleMouseLeave: null,
      handleTouchStart: null,
      handleTouchEnd: null,
      handleBlockBuilderStateChange: null,
      visibilityCheckTimeout: null,
      pendingVisibleComponents: new Set(),
      isTouchDevice: false,

      isMobileOrTablet() {
        // Check if touch is supported
        const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        if (!hasTouch) return false;

        // Exclude devices with mouse/pointer capabilities (laptops with touchscreens)
        const hasMouse = window.matchMedia('(pointer: fine)').matches;
        if (hasMouse) return false;

        // Check user agent for mobile/tablet indicators
        const userAgent = navigator.userAgent.toLowerCase();
        const mobileKeywords = ['mobile', 'tablet', 'android', 'iphone', 'ipad', 'ipod'];
        const hasMobileKeyword = mobileKeywords.some(keyword => userAgent.includes(keyword));

        // Check screen size (mobile/tablet typically under 1024px width)
        const isSmallScreen = window.screen.width <= 1024;

        return hasMobileKeyword || isSmallScreen;
      },

      init() {
        if (!settings.custom_id) return;

        // Detect touch device capability (mobile/tablet only, exclude laptops)
        this.isTouchDevice = this.isMobileOrTablet();

        // Check if overlay mode is enabled
        this.isOverlay = !!settings.enable_header_overlay;
        this.scrollThreshold = settings.scroll_threshold || 0;

        // Check page restrictions for overlay
        if (this.isOverlay) {
          const currentPath = window.location.pathname;
          const isHome = currentPath === '/' || currentPath === '/index.html';
          const isCollection = currentPath.startsWith('/collections/') && !currentPath.includes('/products/');
          const isBlog = currentPath.startsWith('/blogs/');
          const isPage = currentPath.startsWith('/pages/');
          const isProduct =
            currentPath.startsWith('/products/') ||
            (currentPath.startsWith('/collections/') && currentPath.includes('/products/'));

          // Check if any specific page types are enabled for overlay
          const hasSpecificPages =
            settings.transparent_header_home_only ||
            settings.transparent_header_collection_only ||
            settings.transparent_header_blog_only ||
            settings.transparent_header_page_only ||
            settings.transparent_header_product_only;

          if (hasSpecificPages) {
            // If specific page types are selected, only enable overlay on those pages
            const enableOverlay =
              (settings.transparent_header_home_only && isHome) ||
              (settings.transparent_header_collection_only && isCollection) ||
              (settings.transparent_header_blog_only && isBlog) ||
              (settings.transparent_header_page_only && isPage) ||
              (settings.transparent_header_product_only && isProduct);

            this.isOverlay = enableOverlay;
          } else {
            // If no specific pages are selected, disable overlay completely
            this.isOverlay = false;
          }
        }

        if (this.isOverlay) {
          // Check initial scroll state using scroll threshold
          this.hasScrolled = window.scrollY > this.scrollThreshold;
          this.isInverted = !this.hasScrolled && !settings.always_show_bg;
        }
      },

      applyStyles(el) {
        if (!this.isOverlay || !el) {
          return;
        }

        // Apply overlay class - all styles handled by CSS (may already be applied in template)
        el.classList.add('header-overlay');

        // Add CSS custom properties for dynamic values (may already be set in template)
        if (settings.header_bg_color && !el.style.getPropertyValue('--header-bg-color')) {
          el.style.setProperty('--header-bg-color', settings.header_bg_color);
        }

        // Apply initial state classes
        this.updateClasses(el);
      },

      updateClasses(el) {
        if (!this.isOverlay || !el) return;

        const shouldShowBackground =
          this.hasScrolled || this.isHovered || this.hasVisibleHeaderComponents || settings.always_show_bg;
        const shouldInvert = !shouldShowBackground;

        // Toggle CSS classes instead of inline styles
        el.classList.toggle('header-overlay--inverted', shouldInvert);
        el.classList.toggle('header-overlay--scrolled', this.hasScrolled);
        el.classList.toggle('header-overlay--hovered', this.isHovered || this.hasVisibleHeaderComponents);
        el.classList.toggle('header-overlay--always-bg', !!settings.always_show_bg);

        this.isInverted = shouldInvert;
      },

      updateOnScroll(el) {
        if (!this.isOverlay || !el) return;

        // Use scroll threshold instead of body class
        const scrollY = window.scrollY;
        const hasScrolledNow = scrollY > this.scrollThreshold;

        // Only update if scroll state changed
        if (hasScrolledNow !== this.hasScrolled) {
          this.hasScrolled = hasScrolledNow;
          this.updateClasses(el);
        }
      },

      updateOnHover(el, isHovered) {
        if (!this.isOverlay || !el || !settings.enable_hover_bg) return;

        // On touch devices, don't set hover state to prevent sticky hover
        if (this.isTouchDevice && isHovered) return;

        if (isHovered !== this.isHovered) {
          this.isHovered = isHovered;
          this.updateClasses(el);
        }
      },

      clearHoverState(el) {
        if (!this.isOverlay || !el) return;

        if (this.isHovered) {
          this.isHovered = false;
          this.updateClasses(el);
        }
      },

      updateOnBlockBuilderStateChange(el, event) {
        if (!this.isOverlay || !el) return;

        const { state, reparentSelector, componentId } = event.detail;

        // Check if this component is reparented to a header element
        const isHeaderComponent =
          reparentSelector &&
          (reparentSelector.includes('header') ||
            reparentSelector.includes('.transparent-header') ||
            reparentSelector.includes('.header-overlay'));

        if (isHeaderComponent) {
          if (state === 'visible') {
            // Cancel any pending hide checks when a new element becomes visible
            if (this.visibilityCheckTimeout) {
              clearTimeout(this.visibilityCheckTimeout);
              this.visibilityCheckTimeout = null;
            }

            // Add to pending visible components and immediately set hover state
            this.pendingVisibleComponents.add(componentId);
            this.hasVisibleHeaderComponents = true;
            this.updateClasses(el);
          } else {
            // Remove from pending visible components
            this.pendingVisibleComponents.delete(componentId);
            // Use enhanced debounced check that considers pending components
            this.debouncedCheckVisibleHeaderComponents(el);
          }
        }
      },

      checkVisibleHeaderComponents() {
        // Check all header elements for visible block-builder components
        const headerSelectors = ['.transparent-header', 'header', '.header-overlay'];
        let hasVisible = false;
        const actuallyVisibleComponents = new Set();

        headerSelectors.forEach((selector) => {
          const element = document.querySelector(selector);
          if (element) {
            const headerOverlayElements = element.querySelectorAll('[x-data*="blockBuilder"]');
            headerOverlayElements.forEach((el) => {
              if (el.__x && el.__x.$data && el.__x.$data.actionCurrentState === 'visible') {
                hasVisible = true;
                if (el.__x.$data.componentId) {
                  actuallyVisibleComponents.add(el.__x.$data.componentId);
                }
              }
            });
          }
        });

        // Only clear pending components that are now actually visible
        this.pendingVisibleComponents.forEach((componentId) => {
          if (actuallyVisibleComponents.has(componentId)) {
            this.pendingVisibleComponents.delete(componentId);
          }
        });

        // If we have pending components OR actually visible components, maintain hover state
        this.hasVisibleHeaderComponents = hasVisible || this.pendingVisibleComponents.size > 0;
      },

      debouncedCheckVisibleHeaderComponents(el) {
        // Clear any pending timeout
        if (this.visibilityCheckTimeout) {
          clearTimeout(this.visibilityCheckTimeout);
        }

        // Set a short delay to allow rapid state changes to settle
        this.visibilityCheckTimeout = setTimeout(() => {
          this.checkVisibleHeaderComponents();
          this.updateClasses(el);
          this.visibilityCheckTimeout = null;
        }, 30); // Increased delay to ensure animations complete
      },

      bindEvents(el) {
        if (!this.isOverlay || !el) return;

        // Listen for scroll events
        this.handleScroll = () => this.updateOnScroll(el);
        window.addEventListener('scroll', this.handleScroll, { passive: true });

        // Add hover events if enabled
        if (settings.enable_hover_bg) {
          this.handleMouseEnter = () => this.updateOnHover(el, true);
          this.handleMouseLeave = () => this.updateOnHover(el, false);
          el.addEventListener('mouseenter', this.handleMouseEnter);
          el.addEventListener('mouseleave', this.handleMouseLeave);

          // Add touch events to clear hover state on touch devices
          if (this.isTouchDevice) {
            this.handleTouchStart = () => this.clearHoverState(el);
            this.handleTouchEnd = () => this.clearHoverState(el);
            el.addEventListener('touchstart', this.handleTouchStart, { passive: true });
            el.addEventListener('touchend', this.handleTouchEnd, { passive: true });
          }
        }

        // Listen for block-builder state changes
        this.handleBlockBuilderStateChange = (event) => this.updateOnBlockBuilderStateChange(el, event);
        window.addEventListener('block-builder-state-change', this.handleBlockBuilderStateChange);
      }
    };

    return {
      isVisible: true,
      hasSticky: settings.always_show_bg || false,

      init() {
        // Initialize overlay header
        overlayHeader.init();

        // Wait for Alpine to mount the element, then apply styles and events
        this.$nextTick(() => {
          if (this.$el) {
            // Set up header height CSS variable with ResizeObserver
            this.setupHeaderHeightVariable();

            // Apply overlay header styles (if enabled)
            if (overlayHeader.isOverlay) {
              overlayHeader.applyStyles(this.$el);
              overlayHeader.bindEvents(this.$el);
            } else {
              // Remove overlay classes if overlay is disabled
              this.$el.classList.remove('header-overlay');
              this.$el.classList.remove('header-overlay--inverted');
              this.$el.classList.remove('header-overlay--scrolled');
              this.$el.classList.remove('header-overlay--hovered');
              this.$el.classList.remove('header-overlay--always-bg');
            }
          }
        });

        // Legacy scroll background functionality (when overlay is disabled)
        if (!overlayHeader.isOverlay) {
          const scrollThreshold = settings.scroll_threshold || 50;

          const handleScroll = () => {
            const scrollY = window.scrollY;
            if (scrollY >= scrollThreshold) {
              this.hasSticky = true;
            } else {
              this.hasSticky = settings.always_show_bg || false;
            }
          };

          window.addEventListener('scroll', handleScroll, { passive: true });

          // Check initial scroll position
          if (window.scrollY >= scrollThreshold) {
            this.hasSticky = true;
          }
        }
      },

      setupHeaderHeightVariable() {
        // Function to update header height CSS variable
        const updateHeaderHeight = () => {
          if (this.$el) {
            const headerHeight = this.$el.offsetHeight;
            document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
          }
        };

        // Update immediately
        updateHeaderHeight();

        // Set up ResizeObserver to watch for header height changes
        if (window.ResizeObserver) {
          const resizeObserver = new ResizeObserver(updateHeaderHeight);
          resizeObserver.observe(this.$el);
        } else {
          // Fallback for browsers without ResizeObserver support
          window.addEventListener('resize', updateHeaderHeight, { passive: true });
        }
      },

      // Expose overlay header for template access
      overlayHeader
    };
  }
};
